/*
 * partner-attribution.js - Partner attribution on the MAIN storefront (store.anointed.company).
 *
 * A partner's tracked link (https://api.anointedautomation.net/api/partner/go/{slug}?handle=...) logs the click
 * and lands here with ?partner={slug}. This script:
 *   1. keeps the partner for 30 days, LAST CLICK WINS, in localStorage + a first-party cookie, and, for a
 *      signed-in customer, on the backend (GET/PUT /api/partner/attribution, HMAC-signed by Liquid) so the
 *      credit follows the customer to another device;
 *   2. stamps the line item property `mart` = {slug} on EVERY cart line (hidden input on product forms, so
 *      Buy-it-now carries it too, plus /cart/change.js for lines added any other way, re-run after every cart
 *      mutation and right before checkout).
 * `mart` is the same line attribute the mart.club storefronts stamp; the order sync copies it to
 * RawData.lineItems[].MartSlug + Order.MartSlugs and partner earnings pays it. One path for every storefront.
 * Every failure is silent (console.debug): attribution must never break the storefront.
 */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else if (root && root.document) {
    api.boot(root);
  }
}(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  var PARAM = 'partner';
  var PROPERTY = 'mart';
  var STORAGE_KEY = 'aaPartnerAttribution';
  var COOKIE = 'aa_partner';
  var WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
  var SLUG_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

  // ---------- pure helpers (unit tested in partner-attribution.test.js) ----------

  function normalizeSlug(value) {
    if (typeof value !== 'string') { return null; }
    var s = value.trim().toLowerCase();
    return SLUG_RE.test(s) ? s : null;
  }

  function slugFromSearch(search) {
    if (typeof search !== 'string' || search.length === 0) { return null; }
    var q = search.charAt(0) === '?' ? search.slice(1) : search;
    var parts = q.split('&');
    for (var i = 0; i < parts.length; i++) {
      var kv = parts[i].split('=');
      if (decodeURIComponent(kv[0] || '') === PARAM) {
        return normalizeSlug(decodeURIComponent((kv[1] || '').replace(/\+/g, ' ')));
      }
    }
    return null;
  }

  function isLive(rec, nowMs) {
    return !!rec && !!normalizeSlug(rec.slug) && typeof rec.at === 'number'
      && rec.at <= nowMs + 5 * 60 * 1000 && nowMs - rec.at < WINDOW_MS;
  }

  // Last click wins: the live record with the later click time.
  function newer(a, b, nowMs) {
    var la = isLive(a, nowMs);
    var lb = isLive(b, nowMs);
    if (la && lb) { return a.at >= b.at ? a : b; }
    if (la) { return a; }
    if (lb) { return b; }
    return null;
  }

  // The cart lines whose `mart` property is not the current partner, with the merged properties to write.
  function linesToStamp(cart, slug) {
    var out = [];
    var items = (cart && cart.items) ? cart.items : [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var props = it.properties || {};
      if (props[PROPERTY] === slug) { continue; }
      var merged = {};
      for (var k in props) {
        if (Object.prototype.hasOwnProperty.call(props, k)) { merged[k] = props[k]; }
      }
      merged[PROPERTY] = slug;
      out.push({ key: it.key, quantity: it.quantity, properties: merged });
    }
    return out;
  }

  function fromRemote(view) {
    if (!view || !view.Slug || !view.at) { return null; }
    var at = Date.parse(view.at);
    return isNaN(at) ? null : { slug: normalizeSlug(view.Slug), at: at };
  }

  // ---------- browser ----------

  function boot(W) {
    var CFG = W.AAPartnerAttributionConfig || {};
    var API = typeof CFG.apiBase === 'string' ? CFG.apiBase.replace(/\/+$/, '') : '';
    var CUSTOMER_ID = CFG.customerId || null;
    var SIGNATURE = CFG.signature || null;
    var nativeFetch = W.fetch ? W.fetch.bind(W) : null;
    var busy = false;
    var stampTimer = null;
    var current = null;
    var observing = false;

    function dbg() {
      try { W.console.debug.apply(W.console, ['[aa-partner]'].concat([].slice.call(arguments))); } catch (e) { /* silent */ }
    }

    function readLocal() {
      try {
        var raw = W.localStorage.getItem(STORAGE_KEY);
        if (raw) { return JSON.parse(raw); }
      } catch (e) { /* storage blocked */ }
      try {
        var m = W.document.cookie.match(new RegExp('(?:^|; )' + COOKIE + '=([^;]*)'));
        if (m) {
          var parts = decodeURIComponent(m[1]).split('|');
          return { slug: parts[0], at: parseInt(parts[1], 10) };
        }
      } catch (e2) { /* no cookies */ }
      return null;
    }

    function writeLocal(rec) {
      try { W.localStorage.setItem(STORAGE_KEY, JSON.stringify(rec)); } catch (e) { /* storage blocked */ }
      try {
        var maxAge = Math.max(0, Math.floor((rec.at + WINDOW_MS - Date.now()) / 1000));
        W.document.cookie = COOKIE + '=' + encodeURIComponent(rec.slug + '|' + rec.at)
          + '; Max-Age=' + maxAge + '; Path=/; SameSite=Lax; Secure';
      } catch (e2) { /* no cookies */ }
    }

    function clearLocal() {
      try { W.localStorage.removeItem(STORAGE_KEY); } catch (e) { /* storage blocked */ }
      try { W.document.cookie = COOKIE + '=; Max-Age=0; Path=/; SameSite=Lax; Secure'; } catch (e2) { /* no cookies */ }
    }

    function remoteGet() {
      if (!API || !CUSTOMER_ID || !SIGNATURE || !nativeFetch) { return Promise.resolve(null); }
      return nativeFetch(API + '/api/partner/attribution?customerId=' + encodeURIComponent(CUSTOMER_ID)
        + '&signature=' + encodeURIComponent(SIGNATURE), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (body) { return body ? fromRemote(body.Data) : null; })
        .catch(function (e) { dbg('remote get failed', e); return null; });
    }

    function remotePut(rec) {
      if (!API || !CUSTOMER_ID || !SIGNATURE || !nativeFetch) { return Promise.resolve(); }
      return nativeFetch(API + '/api/partner/attribution', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ customerId: CUSTOMER_ID, Signature: SIGNATURE, Slug: rec.slug, clickedAt: new Date(rec.at).toISOString() })
      }).catch(function (e) { dbg('remote put failed', e); });
    }

    function cartFetch(path, body) {
      return nativeFetch(path, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined
      }).then(function (r) { return r.json(); });
    }

    function stampCart() {
      if (!current || busy || !nativeFetch) { return Promise.resolve(); }
      busy = true;
      return cartFetch('/cart.js').then(function (cart) {
        var todo = linesToStamp(cart, current.slug);
        var chain = Promise.resolve();
        todo.forEach(function (line) {
          chain = chain.then(function () {
            return cartFetch('/cart/change.js', { id: line.key, quantity: line.quantity, properties: line.properties })
              .catch(function (e) { dbg('stamp failed', line.key, e); });
          });
        });
        return chain.then(function () { if (todo.length) { dbg('stamped', todo.length, 'line(s)'); } });
      }).catch(function (e) { dbg('cart read failed', e); }).then(function () { busy = false; });
    }

    function scheduleStamp() {
      if (stampTimer) { W.clearTimeout(stampTimer); }
      stampTimer = W.setTimeout(stampCart, 400);
    }

    function stampForms() {
      if (!current) { return; }
      var forms = W.document.querySelectorAll('form[action*="/cart/add"]');
      for (var i = 0; i < forms.length; i++) {
        var f = forms[i];
        var input = f.querySelector('input[name="properties[' + PROPERTY + ']"]');
        if (!input) {
          input = W.document.createElement('input');
          input.type = 'hidden';
          input.name = 'properties[' + PROPERTY + ']';
          f.appendChild(input);
        }
        if (input.value !== current.slug) { input.value = current.slug; }
      }
    }

    function hookFetch() {
      if (!nativeFetch) { return; }
      W.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var p = nativeFetch(input, init);
        if (!busy && /\/cart\/(add|change|update)(\.js)?/.test(url)) {
          p.then(scheduleStamp, scheduleStamp);
        }
        return p;
      };
    }

    function hookCheckout() {
      W.document.addEventListener('submit', function (ev) {
        var form = ev.target;
        if (!current || !form || form.__aaStamped) { return; }
        var action = form.getAttribute('action') || '';
        var isCheckout = /\/(cart|checkout)(\?|$|\/)/.test(action) && form.querySelector('[name="checkout"]');
        if (!isCheckout) { return; }
        ev.preventDefault();
        stampCart().then(function () {
          form.__aaStamped = true;
          var btn = form.querySelector('[name="checkout"]');
          if (form.requestSubmit && btn) { form.requestSubmit(btn); } else { form.submit(); }
        });
      }, true);
      W.document.addEventListener('click', function (ev) {
        var a = ev.target && ev.target.closest ? ev.target.closest('a[href*="/checkout"]') : null;
        if (!current || !a || a.__aaStamped) { return; }
        ev.preventDefault();
        stampCart().then(function () { a.__aaStamped = true; W.location.href = a.href; });
      }, true);
    }

    function activate(rec) {
      current = rec;
      stampForms();
      if (!observing) {
        observing = true;
        try {
          new W.MutationObserver(function () { stampForms(); }).observe(W.document.documentElement, { childList: true, subtree: true });
        } catch (e) { /* old browser */ }
      }
      scheduleStamp();
    }

    var now = Date.now();
    var clicked = slugFromSearch(W.location.search);
    var local = readLocal();
    if (clicked) {
      local = { slug: clicked, at: now };
      writeLocal(local);
    } else if (local && !isLive(local, now)) {
      clearLocal();
      local = null;
    }

    hookFetch();
    hookCheckout();
    if (local) { activate(local); }

    if (CUSTOMER_ID && SIGNATURE) {
      remoteGet().then(function (remote) {
        var best = newer(local, remote, Date.now());
        if (!best) { return; }
        if (best === remote && (!local || remote.slug !== local.slug || remote.at !== local.at)) {
          writeLocal(remote);
          activate(remote);
        } else if (best === local && (!remote || remote.at < local.at)) {
          remotePut(local);
        }
      });
    }
  }

  return {
    boot: boot,
    normalizeSlug: normalizeSlug,
    slugFromSearch: slugFromSearch,
    isLive: isLive,
    newer: newer,
    linesToStamp: linesToStamp,
    fromRemote: fromRemote,
    WINDOW_MS: WINDOW_MS
  };
}));
